import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import {
    IConversationGroup,
    IConversationGroups,
    IConversationListFiltersRequest,
    ICreateThreadResponse,
    IFamilyContacts,
    IGetThreadConversationsByIdRequest,
    IGetThreadConversationsByIdResponse,
    IGetThreadConversationsRequest,
    IThreadListFiltersRequest
} from "../../../resources-vue/vue-interfaces/messaging-service/i-family-portal";
import eventBus from "../../../utilities/eventBus";
import { GroupType, GroupTypeEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../enums/enums";
import { FamilyPortalService } from "../../../services/family-portal-service";
import { PermissionManager } from "../../../common/utilities/permission-manager";

import FPSideNav from "./components/FPSideNav/FPSideNav.vue";
import ScrollPagination from "../../../resources-vue/vue-elements/ScrollPagination/ScrollPagination.vue";
import FPThreadsContainer from "./components/FPThreadsContainer/FPThreadsContainer.vue";
import NewConversation from "./components/NewConversation/NewConversation.vue";

export default defineComponent({
    components: {
        FPSideNav,
        ScrollPagination,
        FPThreadsContainer,
        NewConversation
    },

    computed: {
        allPagesLoaded(): boolean {
            return !!this.pageCount && this.pageNumber > this.pageCount;
        }
    },

    data() {
        return {
            _familyPortalService: null as FamilyPortalService,
            _permissionManager: null as PermissionManager,
            conversationGroupsCount: null as IConversationGroups,
            groupConversations: null as IConversationGroup[],
            selectedGroupType: null as GroupTypeEnum,
            userOs: "" as string,
            term: "" as string,
            pageNumber: 1 as number,
            pageLength: 20 as number,
            pageCount: null as number,
            isLoading: true as boolean,
            isLoadingMoreConversations: false as boolean,
            isLoadingThreads: false as boolean,
            hasError: false as boolean,
            loadingNewGroup: false as boolean,
            selectedConversation: null as IConversationGroup,
            conversationThreadsInfo: null as IGetThreadConversationsByIdResponse,
            openNewConversation: false as boolean,
            familyContacts: null as IFamilyContacts[]
        };
    },

    created() {
        this.userOs = window.sessionStorage.getItem("userOs");
        this._familyPortalService = Container.instance.get(FamilyPortalService);
        this._permissionManager = Container.instance.get(PermissionManager);
        eventBus.on("createThread", this.handleNewThread);
    },

    async mounted() {
        await this.getThreadList();
    },

    watch: {
        selectedGroupType: function (newValue) {
            if (!!newValue) this.getConversationsList();
        },
        selectedConversation: function (newValue, oldValue) {
            if (newValue !== oldValue) {
                this.getConversationThreads(newValue.id);
                this.getContacts();
            }
        }
    },

    methods: {
        async getThreadList() {
            try {
                this.groupConversations = [];
                const filters: IThreadListFiltersRequest = {
                    agencyIds: [],
                    locationIds: [],
                    patientIds: []
                };
                const response = await this._familyPortalService.getThreadsList(filters);
                this.conversationGroupsCount = response.conversationGroups;
                this.selectedGroupType = this.checkGroupPermission();
            } catch (error) {
                console.error(error);
            }
        },

        async getConversationsList() {
            try {
                this.isLoading = true;
                this.loadingNewGroup = true;
                this.openNewConversation = false;
                this.pageNumber = 1;
                this.pageCount = null;
                this.familyContacts = [];
                const payload: IGetThreadConversationsRequest = {
                    page: this.pageNumber,
                    pageLength: this.pageLength,
                    type: this.term.length > 0 ? null : this.selectedGroupType
                };
                const filters: IConversationListFiltersRequest = {
                    searchTerm: this.term,
                    agencyIds: [],
                    locationIds: [],
                    patientIds: []
                };
                const response = await this._familyPortalService.getThreadsConversations(payload, filters);
                this.conversationThreadsInfo = null;
                this.groupConversations = response.conversationGroups ? response.conversationGroups : [];
                if (this.groupConversations?.length > 0) this.selectedConversation = this.groupConversations[0];
                this.pageCount = response.pageCount;
            } catch (error) {
                console.error(error);
                this.hasError = true;
            } finally {
                this.isLoading = false;
                setTimeout(() => {
                    this.loadingNewGroup = false;
                }, 250);
            }
        },

        async updateConversationsList() {
            try {
                this.isLoadingMoreConversations = true;
                // this.isLoading = true;
                this.familyContacts = [];
                const payload: IGetThreadConversationsRequest = {
                    page: this.pageNumber,
                    pageLength: this.pageLength,
                    type: this.term.length > 0 ? null : this.selectedGroupType
                };

                const filters: IConversationListFiltersRequest = {
                    searchTerm: this.term,
                    agencyIds: [],
                    locationIds: [],
                    patientIds: []
                };
                const response = await this._familyPortalService.getThreadsConversations(payload, filters);
                this.groupConversations = response.conversationGroups
                    ? this.groupConversations.concat(response.conversationGroups)
                    : this.groupConversations;
                this.pageCount = response.pageCount;
            } catch (error) {
                console.error(error);
            } finally {
                this.isLoadingMoreConversations = false;
            }
        },

        pageNumberUpdated(pageToLoad: number) {
            if (!pageToLoad || this.loadingNewGroup) {
                return;
            }
            if (pageToLoad != this.pageNumber) {
                this.pageNumber = pageToLoad;
            } else {
                return;
            }
            if (!this.allPagesLoaded) {
                this.updateConversationsList();
            }
        },

        async getConversationThreads(conversationId: string) {
            try {
                this.isLoadingThreads = true;
                const payload: IGetThreadConversationsByIdRequest = {
                    pageLength: 10,
                    newThreadsOnly: false
                };
                const response = await this._familyPortalService.getThreadsConversationsById(conversationId, payload);
                this.conversationThreadsInfo = response;
                if (this.selectedConversation?.isUnread) {
                    await this._familyPortalService.readConversation(conversationId);
                    eventBus.emit("conversationRead", this.selectedConversation);
                    const groupType = GroupType.getKeyUsingValue(this.selectedConversation.conversationGroupType);
                    this.conversationGroupsCount[groupType] = this.conversationGroupsCount[groupType] - 1;
                    this.groupConversations.find((conversation) => conversation.id == conversationId).isUnread = false;
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.isLoadingThreads = false;
            }
        },

        handleNewThread(thread: ICreateThreadResponse) {
            this.openNewConversation = false;
            if (thread.isExistingConversation) {
                if (thread.conversationGroupType != this.selectedGroupType) {
                    this.selectedGroupType = thread.conversationGroupType;
                } else {
                    this.getConversationThreads(thread.conversationGroupId);
                }
            } else {
                this.selectedGroupType = thread.conversationGroupType;
            }
        },

        checkGroupPermission() {
            const isFPSchedulingView = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalScheduling,
                PermissionActionEnum.canView
            );
            if (isFPSchedulingView) return GroupTypeEnum.Scheduling;

            const isFPClinicalView = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalClinical,
                PermissionActionEnum.canView
            );
            if (isFPClinicalView) return GroupTypeEnum.Clinical;

            const isFPBillingView = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalBilling,
                PermissionActionEnum.canView
            );
            if (isFPBillingView) return GroupTypeEnum.Billing;

            const isFPOthersView = this._permissionManager.checkPermission(
                ParentPermissionEnum.familyPortalOthers,
                PermissionActionEnum.canView
            );
            if (isFPOthersView) return GroupTypeEnum.Other;
            return null;
        },

        async getContacts() {
            if (!!this.selectedConversation) {
                try {
                    this.familyContacts = [];
                    const response = await this._familyPortalService.getFamilyContacts(this.selectedConversation.id);
                    this.familyContacts = response.contacts;
                } catch (error) {
                    console.error(error);
                }
            }
        },

        updateChatSearchTerm(value: string) {
            this.term = value;
            this.pageNumber = 1;
            this.getConversationsList();
        }
    },
    beforeUnmount() {
        eventBus.off("createThread", this.handleNewThread);
    }
});
